import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, FormControl, Select, MenuItem, ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import TasksView from '../tasks/TasksView';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import AddAgendaModal from '../myEventDashboard/agendaTab/AddAgendaModal';
import { UserContext } from '../../contexts/UserContext';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import DateNavigation from './DateNavigation';
import { useEventOperationsData } from '../../contexts/EventContext';

const VIEWS = {
  CALENDAR: 'calendar',
  TASKS: 'tasks',
};

const INITIAL_DATE = dayjs();

const CALENDAR_MODES = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
};

const CalendarContent = ({ eventId, refreshAgenda }) => {
  const { user } = useContext(UserContext);
  const { tasks = [] } = useEventOperationsData();
  const [view, setView] = useState(VIEWS.CALENDAR);
  const [calendarMode, setCalendarMode] = useState(CALENDAR_MODES.DAY);
  const [spaces, setSpaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [agendaItems, setAgendaItems] = useState([]);
  const [currentDate, setCurrentDate] = useState(INITIAL_DATE);
  const [editingSpaceId, setEditingSpaceId] = useState(null);
  const [editingSpaceName, setEditingSpaceName] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [addAgendaModalOpen, setAddAgendaModalOpen] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState(null);
  const [addSpaceModalOpen, setAddSpaceModalOpen] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Fetch spaces when component mounts or eventId changes
  useEffect(() => {
    fetchSpaces();
  }, [eventId, refreshAgenda]);

  // Fetch agenda items for the current event id
  useEffect(() => {
    fetchAgendaItems();
  }, [eventId, refreshAgenda]);

  const fetchSpaces = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${getBackendUserUrl()}/agenda/agenda_spaces/${eventId}`);
      const fetchedSpaces = response.data || [];
      
      // If no spaces exist, create a default space
      if (fetchedSpaces.length === 0) {
        const defaultSpace = {
          id: 'default-space',
          name: 'Default Space',
          position: 1,
          isTemporary: true
        };
        setSpaces([defaultSpace]);
      } else {
        setSpaces(fetchedSpaces);
      }
    } catch (error) {
      console.error('Error fetching agenda spaces:', error);
      setSpaces([{
        id: 'default-space',
        name: 'Default Space',
        position: 1,
        isTemporary: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    const baseDate = new Date();
    baseDate.setHours(8, 0, 0, 0);

    for (let i = 0; i < 48; i++) {
      slots.push(format(baseDate, 'HH:mm'));
      baseDate.setMinutes(baseDate.getMinutes() + 30);
    }
    return slots;
  };
  const timeSlots = generateTimeSlots();

  const handleTabChange = (e, newValue) => {
    if (newValue !== null) setView(newValue);
  };

  const handleCalendarModeChange = (event, newMode) => {
    if (newMode !== null) {
      setCalendarMode(newMode);
    }
  };

  const handleAddSpaceClick = () => {
    setNewSpaceName('');
    setAddSpaceModalOpen(true);
  };

  const handleAddSpace = async () => {
    if (!newSpaceName.trim()) return;

    try {
      const safeSpaces = Array.isArray(spaces) ? spaces : [];
      const newSpaceData = {
        event_id: eventId,
        name: newSpaceName.trim(),
        position: safeSpaces.length + 1
      };

      const response = await axios.post(
        `${getBackendUserUrl()}/agenda/agenda_spaces/`,
        newSpaceData
      );

      const updatedSpaces = spaces.filter(space => !space.isTemporary);
      const newSpace = response.data;
      setSpaces([...updatedSpaces, newSpace]);
      setAddSpaceModalOpen(false);
      setNewSpaceName('');
    } catch (error) {
      console.error('Error adding space:', error);
    }
  };

  const handleDeleteSpace = async (spaceId) => {
    try {
      await axios.delete(`${getBackendUserUrl()}/agenda/agenda_spaces/${spaceId}`);
      
      // If this was the last space, add the default placeholder space
      if (spaces.length === 1) {
        const defaultSpace = {
          id: 'default-space',
          name: 'Default Space',
          position: 1,
          isTemporary: true
        };
        setSpaces([defaultSpace]);
      } else {
        setSpaces(spaces.filter(space => space.spaceId !== spaceId));
      }
    } catch (error) {
      console.error('Error deleting space:', error);
    }
  };

  const handleStartEditSpace = (spaceId, currentName) => {
    setEditingSpaceId(spaceId);
    setEditingSpaceName(currentName);
  };

  const handleSaveEditSpace = async (spaceId) => {
    try {
      const response = await axios.patch(
        `${getBackendUserUrl()}/agenda/agenda_spaces/${spaceId}`,
        { name: editingSpaceName.trim() }
      );
      
      setSpaces(spaces.map(space =>
        space.spaceId === spaceId ? { ...space, name: editingSpaceName.trim() } : space
      ));
      setEditingSpaceId(null);
      setEditingSpaceName('');
    } catch (error) {
      console.error('Error updating space:', error);
    }
  };

  const handleCancelEditSpace = () => {
    setEditingSpaceId(null);
    setEditingSpaceName('');
  };

  const handleEditSpaceNameChange = (e) => {
    setEditingSpaceName(e.target.value);
  };

  const handleCellClick = (spaceId, time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const clickedDate = new Date();
    clickedDate.setHours(hours, minutes, 0, 0);
    const formattedTime = format(clickedDate, 'HH:mm');

    // Calculate end time (30 min after start)
    let endHour = hours;
    let endMinute = minutes + 30;
    if (endMinute >= 60) {
      endHour += 1;
      endMinute -= 60;
    }
    const formattedEndTime = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

    setSelectedCell({ 
      spaceId, 
      startTime: formattedTime, 
      endTime: formattedEndTime 
    });
    setEditingAgenda(null);
    setAddAgendaModalOpen(true);
  };

  const handleEditAgenda = (agenda) => {
    setEditingAgenda(agenda);
    setAddAgendaModalOpen(true);
    
    const startMatch = agenda.startTime.match(/(\d{2}):(\d{2}):(\d{2})/);
    const endMatch = agenda.endTime.match(/(\d{2}):(\d{2}):(\d{2})/);
    
    if (startMatch && endMatch) {
      const startTime = `${startMatch[1]}:${startMatch[2]}`;
      setSelectedCell({ 
        spaceId: agenda.spaceId, 
        time: startTime
      });
    } else {
      console.error('Invalid time format:', {
        startTime: agenda.startTime,
        endTime: agenda.endTime
      });
      const now = new Date();
      const currentTime = format(now, 'HH:mm');
      setSelectedCell({ 
        spaceId: agenda.spaceId, 
        time: currentTime
      });
    }
  };

  const handleCloseAddAgendaModal = () => {
    setAddAgendaModalOpen(false);
    setSelectedCell(null);
    setEditingAgenda(null);
  };

  const fetchAgendaItems = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${getBackendUserUrl()}/agenda/agenda_items/${eventId}?include_assignees=false`);
      setAgendaItems(response.data || []);
    } catch (err) {
      console.error('Error fetching agenda items:', err);
      setAgendaItems([]);
    }
  };

  const handleSaveAgenda = (savedAgenda) => {
    fetchAgendaItems();
    handleCloseAddAgendaModal();
  };

  const handleAgendaDeleted = (deletedItemId) => {
    setAgendaItems(prevItems => prevItems.filter(item => item.agendaId !== deletedItemId));
  };


  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleTabChange}
            sx={{
              bgcolor: '#edeaff',
              p: '3px',
              mr: 3,
              borderRadius: '30px',
              border: 'none',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '30px',
                mx: 0.5,
                '&:first-of-type': { ml: 0 },
                '&:last-of-type': { mr: 0 },
              }
            }}
          >
            <ToggleButton
              value={VIEWS.CALENDAR}
              sx={{
                px: 3,
                py: 1,
                bgcolor: view === VIEWS.CALENDAR ? '#6d7eea' : 'transparent',
                color: view === VIEWS.CALENDAR ? '#fff' : '#222',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': { bgcolor: view === VIEWS.CALENDAR ? '#6d7eea' : '#e0e7ff' },
                '&.Mui-selected': { bgcolor: '#6d7eea !important' },
                transition: 'all 0.2s',
              }}
            >
              <CalendarMonthIcon sx={{ mr: 1 }} /> Calendar
            </ToggleButton>
            <ToggleButton
              value={VIEWS.TASKS}
              sx={{
                px: 3,
                py: 1,
                bgcolor: view === VIEWS.TASKS ? '#6d7eea' : 'transparent',
                color: view === VIEWS.TASKS ? '#fff' : '#222',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': { bgcolor: view === VIEWS.TASKS ? '#6d7eea' : '#e0e7ff' },
                '&.Mui-selected': { bgcolor: '#6d7eea !important' },
                transition: 'all 0.2s',
              }}
            >
              <CheckCircleOutlineIcon sx={{ mr: 1 }} /> Tasks
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 100, mr: 2 }}>
            <Select
              value={calendarMode}
              onChange={e => {
                handleCalendarModeChange(e, e.target.value);
              }}
              sx={{ borderRadius: 2, fontWeight: 500 }}
            >
              <MenuItem value={CALENDAR_MODES.DAY}>Day</MenuItem>
              <MenuItem value={CALENDAR_MODES.WEEK}>Week</MenuItem>
              <MenuItem value={CALENDAR_MODES.MONTH} disabled>Month (Coming Soon)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6, mb: 6 }}>
          <Typography variant="h6" sx={{ color: '#64748b' }}>
            Loading spaces...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {view === VIEWS.CALENDAR && (
            <DateNavigation
              view={calendarMode}
              currentDate={currentDate}
              onDateChange={setCurrentDate}
              datePickerOpen={datePickerOpen}
              setDatePickerOpen={setDatePickerOpen}
            />
          )}

          {view === VIEWS.CALENDAR ? (
            <>
              {calendarMode === CALENDAR_MODES.DAY && (
                <DayView
                  spaces={spaces}
                  items={agendaItems}
                  date={currentDate}
                  onDeleteSpace={handleDeleteSpace}
                  onAddSpace={handleAddSpaceClick}
                  editingSpaceId={editingSpaceId}
                  editingSpaceName={editingSpaceName}
                  onStartEditSpace={handleStartEditSpace}
                  onSaveEditSpace={handleSaveEditSpace}
                  onCancelEditSpace={handleCancelEditSpace}
                  onEditSpaceNameChange={handleEditSpaceNameChange}
                  onCellClick={handleCellClick}
                  timeSlots={timeSlots}
                  onEditItem={handleEditAgenda}
                  onAgendaDeleted={handleAgendaDeleted}
                  onAgendaUpdated={fetchAgendaItems}
                  onOptimisticAgendaUpdate={setAgendaItems}
                />
              )}
              {calendarMode === CALENDAR_MODES.WEEK && (
                <WeekView
                  date={currentDate}
                  items={agendaItems}
                  timeSlots={timeSlots}
                  eventId={eventId}
                  onAgendaUpdated={fetchAgendaItems}
                />
              )}
              {calendarMode === CALENDAR_MODES.MONTH && (
                <MonthView
                  date={currentDate}
                />
              )}
            </>
          ) : (
            <TasksView />
          )}
        </Box>
      )}

      <AddAgendaModal
        open={addAgendaModalOpen}
        onClose={handleCloseAddAgendaModal}
        onSave={handleSaveAgenda}
        eventId={eventId}
        spaceId={selectedCell?.spaceId}
        initialData={editingAgenda ? {
          agendaId: editingAgenda.agendaId,
          name: editingAgenda.name,
          description: editingAgenda.description,
          startTime: editingAgenda.startTime ? editingAgenda.startTime.substring(0, 5) : '',
          endTime: editingAgenda.endTime ? editingAgenda.endTime.substring(0, 5) : '',
          spaceId: editingAgenda.spaceId,
          userId: user?.userId
        } : { 
          startTime: selectedCell?.startTime, 
          endTime: selectedCell?.endTime,
          spaceId: selectedCell?.spaceId,
          userId: user?.userId
        }}
        isEdit={!!editingAgenda}
        tasks={tasks}
      />

      <Dialog open={addSpaceModalOpen} onClose={() => setAddSpaceModalOpen(false)}>
        <DialogTitle>Add New Space</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 320, pt: 2 }}>
          <TextField
            label="Space Name"
            value={newSpaceName}
            onChange={(e) => setNewSpaceName(e.target.value)}
            autoFocus
            placeholder="e.g., Main Hall, VIP Room"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSpaceModalOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddSpace}
            variant="contained"
            disabled={!newSpaceName.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const EventCalendar = ({ eventId, refreshAgenda }) => {
  return <CalendarContent eventId={eventId} refreshAgenda={refreshAgenda} />;
};

export default EventCalendar; 